import { Button, EmptyState } from '@shared/ui';
import { useEffect, useState } from 'react';

import { describeFailure, retryAvailableAt } from '../model/failure';

import { EmployeeSignInPanel, PermissionRequiredPanel } from './EmployeeSignInPanel';

/**
 * A failed read, by kind (see `describeFailure`). Never the empty state. Each
 * "Try again" re-issues the same query once.
 */
export function FailurePanel({
    error,
    failedAt,
    onRetry,
}: {
    error: Error;
    failedAt: number;
    onRetry: () => void;
}) {
    const view = describeFailure(error);

    if (view.kind === 'sign-in') return <EmployeeSignInPanel />;
    if (view.kind === 'forbidden') return <PermissionRequiredPanel />;
    if (view.kind === 'invalid') {
        return <EmptyState title="The log refused these filters" description={view.message} />;
    }
    if (view.kind === 'rate-limited') {
        return (
            <RateLimited
                message={view.message}
                availableAt={retryAvailableAt(failedAt, view.retryAfterSeconds)}
                onRetry={onRetry}
            />
        );
    }
    return (
        <EmptyState
            title="Couldn't load the refusal log"
            description="The audit log did not answer. Nothing is wrong with your filters."
            action={
                <Button variant="ghost" onClick={onRetry}>
                    Try again
                </Button>
            }
        />
    );
}

function RateLimited({
    message,
    availableAt,
    onRetry,
}: {
    message: string;
    availableAt: number;
    onRetry: () => void;
}) {
    const [now, setNow] = useState(() => Date.now());
    const waiting = now < availableAt;

    useEffect(() => {
        if (!waiting) return undefined;
        const timer = setTimeout(() => setNow(Date.now()), Math.max(0, availableAt - now));
        return () => clearTimeout(timer);
    }, [availableAt, now, waiting]);

    return (
        <EmptyState
            title="Too many queries"
            description={message}
            action={
                <Button variant="ghost" onClick={onRetry} disabled={waiting}>
                    Try again
                </Button>
            }
        />
    );
}
